/**
 * The CRM viewer's RPC read allowlist (brief H rulings 2 and 4, notes 04 §2).
 *
 * The four screens the first build cut (Documents, Settings Firm storage, the
 * client record's Files and Forms tabs) read through seven platform RPC groups
 * that resolve identity on the shared RPC lane. Under a view, a call passes
 * only when its group is named here AND its tag is in that group's set. Every
 * other tag, every write included, is refused twice: by the browser delegate
 * before a request leaves (`scoped-fetch.ts`) and by the door on the server
 * before a statement runs (`StaffViewRpcMiddleware`). One list, imported by
 * both sides, so the two refusals cannot disagree.
 *
 * WHAT IS IN IT. The 21 reads notes 04 §2 measured the four screens calling,
 * minus `searchByContent`, which embeds every query through Cohere and so
 * costs money per call (ruling 2), and minus `getVersionDownloadUrl`: 19 tags
 * across 7 groups. A member's preview or download writes its own `opened`
 * or `downloaded` row on the firm's reads chain; the files writer refuses a
 * staff actor there because a staff read is the staff trail's alone (audit
 * coverage spec 2026-10-11-001 §3.3), so under a view it is refused at the
 * door rather than dying in the handler. Letting staff preview files under a
 * view means routing that row to the staff trail first, a files change.
 *
 * WHAT IS NOT. `ClientFormsRpc` and `FormSendRpc` contribute no read: the
 * Forms tab's own read is the BMS lane's `getClientForms` (notes 04 §3). Their
 * paths stay under the delegate's refused `/api/rpc/form` prefix.
 *
 * Pure data, browser-safe: no import, so the delegate can carry it.
 */

/** The seven groups by their contract's export name, each with the tags a view may call. */
export const STAFF_VIEW_RPC_READS = {
  FilesRpc: new Set([
    "listChildren",
    "listFoldersForClientPage",
    "countFilesByFolderForClient",
    "countFilesByFacet",
    "listFilesByFilterPage",
    "getNode",
    "getNodeWithLatestVersion",
    "listVersions",
    "searchByName",
    "listNodeGrants",
  ]),
  CategoriesRpc: new Set(["listFileCategories", "countNodesUsingCategory"]),
  OrgTagsRpc: new Set(["listFileOrgTags", "countNodesUsingTag"]),
  StarsRpc: new Set(["listMyStarredNodes"]),
  FilesActivityRpc: new Set(["listOrgEvents", "listOrgGrantSummary"]),
  FormsRpc: new Set(["listFormsByOrg"]),
  FormCategoriesRpc: new Set(["listFormCategories"]),
} as const satisfies Readonly<Record<string, ReadonlySet<string>>>

/** One of the seven allowlisted groups. */
export type StaffViewRpcGroup = keyof typeof STAFF_VIEW_RPC_READS

/**
 * Each route prefix the seven groups mount on, to its group (notes 04 §2's
 * route column). `FilesRpc` is mounted twice, at the BMS path the firm's
 * slices call and at the platform path, and both are listed so a view
 * reaching either is judged by the same set.
 */
export const STAFF_VIEW_RPC_PREFIXES = {
  "/api/rpc/bms/files": "FilesRpc",
  "/api/rpc/files": "FilesRpc",
  "/api/rpc/file-categories": "CategoriesRpc",
  "/api/rpc/file-org-tags": "OrgTagsRpc",
  "/api/rpc/file-stars": "StarsRpc",
  "/api/rpc/files-activity": "FilesActivityRpc",
  "/api/rpc/form/forms": "FormsRpc",
  "/api/rpc/form/categories": "FormCategoriesRpc",
} as const satisfies Readonly<Record<string, StaffViewRpcGroup>>
